import React, { useEffect, useState } from 'react';

import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import { CustomReportFilesDTO } from '@/components/ui/Inputs/types/response/customReport';
import { BeraJobSummaryDTO } from '@/components/views/BeraJob/hooks/types/response';
import { BERA_REPORT_VERSION } from '@/components/views/BeraJob/constants';

function getPageFileIds(beraFiles: CustomReportFilesDTO | undefined): Set<string> {
	return new Set((beraFiles?.files?.rows || []).map((f) => String(f.id)));
}

function getBoundFileIds(rows: FileForCustomReport[], jobSummaryId: string): string[] {
	return rows
		.filter(
			(file) =>
				file.bera_job_summary_files?.bera_job_summary_id === jobSummaryId ||
				file.bera_report?.some((report) => report.bera_job_summary_id === jobSummaryId)
		)
		.map((file) => String(file.id));
}

function upgradeSyntheticFiles(files: FileForCustomReport[], beraFiles: CustomReportFilesDTO): FileForCustomReport[] {
	const currentPageMap = new Map((beraFiles.files.rows || []).map((f) => [String(f.id), f]));
	return files.map((file) => currentPageMap.get(String(file.id)) || file);
}

function getDeselectedIds(currentPageIds: Set<string>, selectedSet: Set<string>): Set<string> {
	return new Set(Array.from(currentPageIds).filter((id) => !selectedSet.has(id)));
}

const SINGLE_FILE_LIMIT = 1;

function limitToSingleFile<T>(items: T[], isSingleSelection: boolean): T[] {
	if (!isSingleSelection) {
		return items;
	}

	const result = items.slice(0, SINGLE_FILE_LIMIT);

	return result;
}

function dropFilesFromOtherPages(
	files: FileForCustomReport[],
	selectedSet: Set<string>,
	isSingleSelection: boolean
): FileForCustomReport[] {
	if (!isSingleSelection) {
		return files;
	}

	const result = files.filter((file) => selectedSet.has(String(file.id)));

	return result;
}

interface UseAutoSelectFilesParams {
	beraFiles: CustomReportFilesDTO;
	beraJobSummary: BeraJobSummaryDTO | undefined;
	selectedFilesId: string[] | React.Key[];
	filterSelectedFiles: (selectedRows: React.Key[], beraFiles: CustomReportFilesDTO) => FileForCustomReport[];
	handleSelectedFilesChange: (files: FileForCustomReport[]) => void;
}

export function useAutoSelectFiles({
	beraFiles,
	beraJobSummary,
	selectedFilesId,
	filterSelectedFiles,
	handleSelectedFilesChange
}: UseAutoSelectFilesParams) {
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

	const isSingleSelection = beraJobSummary?.version === BERA_REPORT_VERSION.V2;

	useEffect(() => {
		const currentPageIds = getPageFileIds(beraFiles);
		const keysInCurrentPage = (selectedFilesId || []).map(String).filter((id) => currentPageIds.has(id));
		setSelectedRowKeys(keysInCurrentPage);
	}, [beraFiles?.files?.rows, selectedFilesId]);

	useEffect(() => {
		if (!beraFiles?.files?.rows || !beraJobSummary?.id || hasUserInteracted) {
			return;
		}

		const boundIds = getBoundFileIds(beraFiles.files.rows, beraJobSummary.id);
		const selectedIds = new Set((selectedFilesId || []).map(String));
		const toAdd = boundIds.filter((id) => !selectedIds.has(id));

		if (toAdd.length === 0) {
			return;
		}

		const mergedIds = Array.from(new Set([...Array.from(selectedIds.values()), ...toAdd]));
		const allowedIds = limitToSingleFile(mergedIds, isSingleSelection);
		const mergedFiles = filterSelectedFiles(allowedIds, beraFiles);
		const upgradedFiles = upgradeSyntheticFiles(mergedFiles, beraFiles);

		handleSelectedFilesChange(upgradedFiles);
	}, [beraFiles?.files?.rows, beraJobSummary?.id, selectedFilesId, hasUserInteracted, isSingleSelection]);

	function handleSelectChange(rows: React.Key[]) {
		const selectedRows = limitToSingleFile(rows, isSingleSelection);

		setHasUserInteracted(true);
		setSelectedRowKeys(selectedRows);

		const selectedSet = new Set(selectedRows.map(String));
		const currentPageIds = getPageFileIds(beraFiles);
		const deselectedIds = getDeselectedIds(currentPageIds, selectedSet);

		const mergedFiles = filterSelectedFiles(selectedRows, beraFiles).filter(
			(f) => !deselectedIds.has(String(f.id))
		);
		const files = dropFilesFromOtherPages(mergedFiles, selectedSet, isSingleSelection);

		handleSelectedFilesChange(files);
	}

	function isFileDisabled(file: FileForCustomReport): boolean {
		if (isSingleSelection) {
			return false;
		}

		const result = !!file.bera_report?.some((report) => report.bera_job_summary_id === beraJobSummary?.id);

		return result;
	}

	function handleRowSelect(file: FileForCustomReport): void {
		if (isFileDisabled(file)) {
			return;
		}

		const fileKey = String(file.id);

		if (isSingleSelection) {
			handleSelectChange([fileKey]);
			return;
		}

		const isSelected = selectedRowKeys.some((key) => String(key) === fileKey);
		let nextKeys: React.Key[];

		if (isSelected) {
			nextKeys = selectedRowKeys.filter((key) => String(key) !== fileKey);
		} else {
			nextKeys = [...selectedRowKeys, fileKey];
		}

		handleSelectChange(nextKeys);
	}

	return { selectedRowKeys, handleSelectChange, handleRowSelect, isFileDisabled, isSingleSelection };
}
